import {useCallback, useMemo} from 'react'
import FileUploader from "components/form/FileUploader";
import FormLabel from "components/form/FormLabel";
import ajax from "config/ajax.ts";
import Icon from "components/atom/Icon.tsx";
import {UploadImageType} from "globals/types/globals.ts";
import {getUrlFromFormat} from "globals/functions/image.ts";
import {useTranslation} from "react-i18next";


type ImageType = { imagePath: string, imageUrl: string, formats: number[] | null }

type PropsType = {
    values: ImageType[]
    onUpload: (images: ImageType[]) => void
    uploadFolder: string
    onDelete: (idx: number) => void
    disabled?: boolean
}

function FormImage(props: PropsType) {
    const {t} = useTranslation()

    const {
        disabled,
        values,
        uploadFolder,
        onUpload,
        onDelete
    } = props


    const uploadImage = useCallback((file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', uploadFolder)
        return ajax.post('/connected/globals/upload', formData)
            .then(res => res.data) as Promise<UploadImageType>
    }, [uploadFolder]);

    const handleMultipleAddFiles = useCallback((files: File[]) => {
        const promises = []
        for (const file of files) {
            promises.push(uploadImage(file))
        }
        return Promise.all(promises)
            .then((filesData) => {
                const images = []
                for (const fileData of filesData) {
                    images.push({imageUrl: fileData.url, imagePath: fileData.path, formats: null})
                }
                onUpload(images)
                return Promise.resolve()
            })
    }, [onUpload, uploadImage])

    const onDownload = useCallback((idx: number) => {
        const imageUrl = values[idx].imageUrl
        const extension = imageUrl.split('.').pop()
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `${imageUrl.split('/').pop()}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [values])

    return (
        <div className="c__input">
            <div className="flex w-full flex-col items-center justify-center">
                <FormLabel htmlFor="images" className="self-start">
                    {t('forms.coverImage')}
                </FormLabel>

                <FileUploader
                    disabled={disabled}
                    handleFiles={handleMultipleAddFiles}
                    removeFile={() => onDelete(0)}>
                    <div className={`input flex flex-row items-center justify-start gap-4 ${disabled ? '-disabled' : ''}`}>
                        <Icon name="upload" className="h-6 w-6 min-w-6"/>
                        <span>
                            {t('forms.includeFile')}
                        </span>
                    </div>
                </FileUploader>
                <FormLabel htmlFor="images" className="mt-3 self-start">
                    {t('forms.images')}
                </FormLabel>

                <div className="mb-6 mt-1 flex w-full flex-row items-center justify-start gap-6">
                    {values.length === 0 && (
                        <p className="text-xs">
                            {t('forms.noImage')}
                        </p>
                    )}

                    {values.map((image: ImageType, idx: number) => (
                            <PreviewImageCard
                                key={idx}
                                image={image}
                                idx={idx}
                                disabled={disabled}
                                onDownload={onDownload}
                                onDelete={(idx) => {
                                    if (disabled) return
                                    onDelete(idx)
                                }}/>
                        )
                    )}
                </div>

            </div>
        </div>
    )
}

function PreviewImageCard(props: {
    image: ImageType,
    idx: number,
    disabled?: boolean,
    onDelete: (idx: number) => void,
    onDownload: (idx: number) => void
}) {
    const {image, idx, onDelete, onDownload, disabled = false} = props

    const imageUrl = useMemo(() => {
        let imageUrl = image.imageUrl
        if (imageUrl) {
            if (image.formats) {
                if (image.formats.length > 0) {
                    imageUrl = getUrlFromFormat(imageUrl, image.formats, 100)
                }
            }
        }
        return imageUrl
    }, [image])

    return (
        <div className="min-24 group relative h-24 w-24 overflow-hidden rounded-md bg-gray-200">
            <img alt="" src={imageUrl} className="absolute inset-0 block h-full w-full object-contain object-center"/>

            <div
                style={{background: 'rgba(0,0,0,0.3)'}}
                className={`pointer-events-none absolute inset-0 flex h-full w-full flex-row items-center justify-center gap-2 opacity-0 transition duration-100 ease-in group-hover:pointer-events-auto group-hover:opacity-100 ${disabled ? '' : 'cursor-pointer'}`}>

                <button onClick={() => onDownload(idx)}
                        className="flex appearance-none flex-col items-center justify-center rounded-full bg-white p-1"
                        type="button">
                    <Icon name="arrow-down" className="h-4 w-4 text-black"/>
                </button>

                <button onClick={() => onDelete(idx)}
                        className={`flex appearance-none flex-col items-center justify-center rounded-full p-1 ${disabled ? 'cursor-auto pointer-event-none bg-gray-200' : 'bg-white'}`}
                        type="button">
                    <Icon name="x" className="h-4 w-4 text-black"/>
                </button>


            </div>
        </div>
    )
}


export default FormImage
